import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CONTACT_DRAWER_QUERY_KEYS } from './contact-drawer-url-state';

/**
 * URL params written by the now-removed affiliate and creator drawers. Kept as
 * plain string literals (not imported) because their source modules are deleted:
 * this is a frozen bookmarked-URL contract, not live state.
 */
const LEGACY_AFFILIATE_KEYS = ['affiliateUser', 'mode', 'program', 'from'] as const;
const LEGACY_CREATOR_KEYS = [
  'creator',
  'creatorUsername',
  'creatorSource',
  'creatorPlatformUser',
  'creatorName',
  'creatorAvatar',
] as const;
const ALL_LEGACY_KEYS: readonly string[] = [...LEGACY_AFFILIATE_KEYS, ...LEGACY_CREATOR_KEYS];

/**
 * One-shot translation of old bookmarked drawer URLs onto the unified contact
 * drawer params, then purges every legacy key so the URL self-heals.
 *
 * - `affiliateUser=<userId>` -> `contactId=<userId>` + `contactEntry=affiliate`
 *   (with `mode=config` + `program=<id>` -> `contactView=affiliation` +
 *   `contactCollab=<id>`).
 * - `creatorUsername=<u>` -> `contactId=<u>` + `contactEntry=creator` (with
 *   `creatorSource` -> `contactSourceCreatorId`).
 * - The internal-id `creator=<id>` param has no username to resolve against, so
 *   it is only purged (no drawer opens).
 *
 * Writes with `replace` to avoid a history entry. Mirrors the self-heal pattern
 * in `GlobalContactDrawer`.
 */
export const useLegacyContactParamRedirect = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // `program` is now also owned by the affiliate-import deep link. It was
    // never sufficient on its own to identify a legacy contact drawer, so only
    // run the migration when an unambiguous legacy identifier is present.
    // Without this guard the global drawer mounts after the import page and
    // silently strips `?program=` from terminal import reports.
    const hasLegacyAffiliate = searchParams.has('affiliateUser');
    const hasLegacyCreator = LEGACY_CREATOR_KEYS.some((key) => searchParams.has(key));
    const hasLegacyParam = hasLegacyAffiliate || hasLegacyCreator;
    if (!hasLegacyParam) return;

    const next = new URLSearchParams(searchParams);
    for (const key of ALL_LEGACY_KEYS) next.delete(key);

    const affiliateUser = searchParams.get('affiliateUser');
    const creatorUsername = searchParams.get('creatorUsername');

    if (affiliateUser) {
      next.set(CONTACT_DRAWER_QUERY_KEYS.contactId, affiliateUser);
      next.set(CONTACT_DRAWER_QUERY_KEYS.contactEntry, 'affiliate');
      const program = searchParams.get('program');
      if (searchParams.get('mode') === 'config' && program) {
        next.set(CONTACT_DRAWER_QUERY_KEYS.contactView, 'affiliation');
        next.set(CONTACT_DRAWER_QUERY_KEYS.contactCollab, program);
      }
    } else if (creatorUsername) {
      next.set(CONTACT_DRAWER_QUERY_KEYS.contactId, creatorUsername);
      next.set(CONTACT_DRAWER_QUERY_KEYS.contactEntry, 'creator');
      const creatorSource = searchParams.get('creatorSource');
      if (creatorSource) {
        next.set(CONTACT_DRAWER_QUERY_KEYS.contactSourceCreatorId, creatorSource);
      }
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
};
